import { Dispatch } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store, createStore } from 'redux';
import { ActionType } from './ActionTypes';
import { GeolocationAction, geolocationReducer } from './geolocation/GeolocationActions';
import { GeolocationState, INITIAL_GEOLOCATION_STATE } from './geolocation/GeolocationState';
import { PageStackAction, PageStackSelector, pageStackReducer } from './pageStack/PageStackActions';
import { INITIAL_PAGE_STACK_STATE, PageStackState } from './pageStack/PageStackState';
import { PapAction, papReducer } from './pap/PapActions';
import { INITIAL_PAP_STATE, PapState } from './pap/PapState';
import { PricingAction, PrivatePricingAction, pricingReducer } from './pricing/PricingActions';
import { INITIAL_PRICING_STATE, PricingState } from './pricing/PricingState';
import { PrivatePurchaseAction, PurchaseAction, purchaseReducer } from './purchase/PurchaseActions';
import { INITIAL_PURCHASE_STATE, PurchaseState } from './purchase/PurchaseState';
import { ReferralAction, referralReducer } from './referral/ReferralActions';
import { INITIAL_REFERRAL_STATE, ReferralState } from './referral/ReferralState';
import { PrivateUs65PlusAction, Us65PlusAction, us65PlusReducer } from './us65Plus/Us65PlusActions';
import { INITIAL_US_65_PLUS_STATE, Us65PlusState } from './us65Plus/Us65PlusState';
import { PrivateUserAction, PublicUserAction, userReducer } from './user/UserActions';
import { UserSelector } from './user/UserSelector';
import { INITIAL_USER_STATE, UserState } from './user/UserState';
import { UserDataAction, UserDataSelector, userDataReducer } from './userData/UserDataActions';
import { INITIAL_USER_DATA_STATE, UserDataState } from './userData/UserDataState';

export type PrivateRestoreStoreAction = {
  type: ActionType.RESTORE_STORE;
  persistedStore: Partial<StoreStateInternal>;
};

export type PublicStoreAction =
  | PageStackAction
  | UserDataAction
  | PublicUserAction
  | GeolocationAction
  | PricingAction
  | PurchaseAction
  | ReferralAction
  | PapAction
  | Us65PlusAction;

export type PrivateStoreAction =
  | PrivateRestoreStoreAction
  | PrivateUserAction
  | PrivatePricingAction
  | PrivatePurchaseAction
  | PrivateUs65PlusAction;

export type StoreAction = PublicStoreAction | PrivateStoreAction;
export type StoreType = Store<StoreStateInternal, StoreAction>;
export type AppStore = Store<StoreStateInternal, StoreAction>;

export type StoreStateInternal = {
  pageStack: PageStackState;
  user: UserState;
  userData: UserDataState;
  geolocation: GeolocationState;
  pricing: PricingState;
  purchase: PurchaseState;
  referral: ReferralState;
  pap: PapState;
  us65Plus: Us65PlusState;
};

export const INITIAL_STATE: StoreStateInternal = {
  pageStack: INITIAL_PAGE_STACK_STATE,
  user: INITIAL_USER_STATE,
  userData: INITIAL_USER_DATA_STATE,
  geolocation: INITIAL_GEOLOCATION_STATE,
  pricing: INITIAL_PRICING_STATE,
  purchase: INITIAL_PURCHASE_STATE,
  referral: INITIAL_REFERRAL_STATE,
  pap: INITIAL_PAP_STATE,
  us65Plus: INITIAL_US_65_PLUS_STATE,
};

export function reducer(
  state: StoreStateInternal = INITIAL_STATE,
  action: StoreAction
): StoreStateInternal {
  if (action.type === ActionType.RESTORE_STORE) {
    return {
      ...state,
      ...action.persistedStore,
      pricing: {
        ...state.pricing,
        ...action.persistedStore.pricing,
        // PAP coupon codes take priority as they were earned through a completed flow.
        // Otherwise, coupons in query params override persisted store coupons.
        couponCode:
          action.persistedStore.pap?.scoreResult !== undefined
            ? (action.persistedStore.pricing?.couponCode ?? state.pricing.couponCode)
            : (state.pricing.couponCode ?? action.persistedStore.pricing?.couponCode),
      },
    };
  }
  return {
    pageStack: pageStackReducer(state.pageStack, action),
    geolocation: geolocationReducer(state.geolocation, action),
    user: userReducer(state.user, action),
    userData: userDataReducer(state.userData, action),
    pricing: pricingReducer(state.pricing, action),
    purchase: purchaseReducer(state.purchase, action),
    referral: referralReducer(state.referral, action),
    pap: papReducer(state.pap, action),
    us65Plus: us65PlusReducer(state.us65Plus, action),
  };
}

/**
 * React hook for FunctionComponents to retrieve state from store. Stronger typing than useSelector.
 * `useAppStore` name chosen to avoid conflict with redux's `useStore` hook
 */
export function useAppStore<Selected>(
  selector: (state: StoreStateInternal) => Selected,
  equalityFn?: (left: Selected, right: Selected) => boolean
): Selected {
  return useSelector<StoreStateInternal, Selected>(selector, equalityFn);
}

/**
 * React hook for FunctionComponents to dispatch app actions to the store. Stronger typing than useDispatch.
 */
export function useAppDispatch(): Dispatch<PublicStoreAction> {
  return useDispatch<Dispatch<PublicStoreAction>>();
}

export const AppSelector = {
  ...PageStackSelector,
  ...UserSelector,
  ...UserDataSelector,
};

export const store = createStore(reducer, window.__REDUX_DEVTOOLS_EXTENSION__?.());
